import React, { useEffect } from 'react';
import { X, CheckCircle } from 'lucide-react';
import { usePWA } from '@/contexts/PWAContext';

function detectIOS() {
  return /iphone|ipad|ipod/i.test(navigator.userAgent) && !('MSStream' in window);
}
function detectSamsungBrowser() {
  return /SamsungBrowser/i.test(navigator.userAgent);
}

/**
 * Guia de instalação universal — iOS Safari, Android Chrome e Samsung Browser.
 */
export default function IOSInstallModal() {
  const { showIOSModal, setShowIOSModal, confirmIOSInstall } = usePWA();

  useEffect(() => {
    if (showIOSModal) document.body.style.overflow = 'hidden';
    else document.body.style.overflow = '';
    return () => { document.body.style.overflow = ''; };
  }, [showIOSModal]);

  if (!showIOSModal) return null;

  const isIOS     = detectIOS();
  const isSamsung = detectSamsungBrowser();

  // ── Passos consoante o dispositivo ──────────────────────────────────────────
  const steps = isIOS ? [
    {
      num: '1',
      color: 'blue',
      title: 'Toca no botão Partilhar',
      sub: 'Na barra inferior do Safari',
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="#0b1e44" strokeWidth="2.5"
          strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5">
          <path d="M4 12v8a2 2 0 002 2h12a2 2 0 002-2v-8" />
          <polyline points="16 6 12 2 8 6" />
          <line x1="12" y1="2" x2="12" y2="15" />
        </svg>
      ),
      iconLabel: 'Partilhar',
      animation: 'ios-bounce-arrow 1.4s ease-in-out infinite',
    },
    {
      num: '2',
      color: 'amber',
      title: 'Toca em «Adicionar ao Ecrã Principal»',
      sub: 'Desliza o menu de partilha para encontrar a opção',
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="#b45309" strokeWidth="2.5"
          strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5">
          <rect x="3" y="3" width="7" height="7" rx="1" />
          <rect x="14" y="3" width="7" height="7" rx="1" />
          <rect x="3" y="14" width="7" height="7" rx="1" />
          <path d="M17 17h4M19 15v4" />
        </svg>
      ),
      iconLabel: 'Adicionar',
      animation: 'ios-pulse-badge 1.6s ease-in-out infinite 0.4s',
    },
  ] : isSamsung ? [
    {
      num: '1',
      color: 'blue',
      title: 'Toca no menu ☰ (Samsung Internet)',
      sub: 'Canto inferior direito ou superior direito do browser',
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="#0b1e44" strokeWidth="2.5"
          strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5">
          <line x1="3" y1="6" x2="21" y2="6"/>
          <line x1="3" y1="12" x2="21" y2="12"/>
          <line x1="3" y1="18" x2="21" y2="18"/>
        </svg>
      ),
      iconLabel: 'Menu',
      animation: 'ios-bounce-arrow 1.4s ease-in-out infinite',
    },
    {
      num: '2',
      color: 'amber',
      title: 'Toca em «Adicionar página a» → «Ecrã principal»',
      sub: 'A app será instalada no teu ecrã principal',
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="#b45309" strokeWidth="2.5"
          strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5">
          <rect x="3" y="3" width="7" height="7" rx="1" />
          <rect x="14" y="3" width="7" height="7" rx="1" />
          <rect x="3" y="14" width="7" height="7" rx="1" />
          <path d="M17 17h4M19 15v4" />
        </svg>
      ),
      iconLabel: 'Instalar',
      animation: 'ios-pulse-badge 1.6s ease-in-out infinite 0.4s',
    },
  ] : /* Android Chrome */ [
    {
      num: '1',
      color: 'blue',
      title: 'Toca nos três pontos ⋮ no Chrome',
      sub: 'Canto superior direito do browser',
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="#0b1e44" strokeWidth="2.5"
          strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5">
          <circle cx="12" cy="5" r="1.2" fill="#0b1e44"/>
          <circle cx="12" cy="12" r="1.2" fill="#0b1e44"/>
          <circle cx="12" cy="19" r="1.2" fill="#0b1e44"/>
        </svg>
      ),
      iconLabel: 'Menu ⋮',
      animation: 'ios-bounce-arrow 1.4s ease-in-out infinite',
    },
    {
      num: '2',
      color: 'amber',
      title: 'Toca em «Instalar app» ou «Adicionar ao ecrã»',
      sub: 'A app instala em segundos, sem App Store',
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="#b45309" strokeWidth="2.5"
          strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5">
          <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/>
          <polyline points="7 10 12 15 17 10"/>
          <line x1="12" y1="15" x2="12" y2="3"/>
        </svg>
      ),
      iconLabel: 'Instalar',
      animation: 'ios-pulse-badge 1.6s ease-in-out infinite 0.4s',
    },
  ];

  const ctaText = isIOS
    ? 'Segue os 2 passos acima no Safari'
    : isSamsung
      ? 'Segue os 2 passos no Samsung Internet'
      : 'Segue os 2 passos acima no Chrome';

  const colorMap = {
    blue:  { bg: 'bg-blue-50',  border: 'border-blue-100',  circle: 'bg-[#0b1e44]', iconBorder: 'border-blue-500'  },
    amber: { bg: 'bg-amber-50', border: 'border-amber-100', circle: 'bg-amber-500',  iconBorder: 'border-amber-400' },
  };

  return (
    <>
      <style>{`
        @keyframes ios-sheet-up {
          from { transform: translateY(100%); opacity: 0; }
          to   { transform: translateY(0);    opacity: 1; }
        }
        @keyframes ios-bounce-arrow {
          0%, 100% { transform: translateY(0); }
          50%       { transform: translateY(5px); }
        }
        @keyframes ios-pulse-badge {
          0%, 100% { opacity: 1; transform: scale(1); }
          50%       { opacity: 0.85; transform: scale(1.06); }
        }
      `}</style>

      {/* Overlay */}
      <div
        className="fixed inset-0 z-[60] bg-black/50 backdrop-blur-sm"
        onClick={() => setShowIOSModal(false)}
        data-testid="ios-modal-overlay"
      />

      {/* Sheet */}
      <div
        className="fixed inset-x-0 bottom-0 z-[61] rounded-t-3xl bg-white shadow-2xl"
        style={{ animation: 'ios-sheet-up 0.35s cubic-bezier(0.32, 0.72, 0, 1)' }}
        data-testid="ios-install-modal"
      >
        {/* Handle */}
        <div className="flex justify-center pt-3 pb-1">
          <div className="w-10 h-1 rounded-full bg-gray-300" />
        </div>

        {/* Header */}
        <div className="flex items-center justify-between px-5 pt-2 pb-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-2xl bg-[#0b1e44] flex items-center justify-center shadow-lg">
              <img src="/icons/icon-96x96.png" alt="Trabadju" className="w-8 h-8 object-contain"
                onError={e => { e.target.style.display = 'none'; }} />
            </div>
            <div>
              <p className="font-bold text-gray-900 text-base leading-tight">Instalar Trabadju</p>
              <p className="text-xs text-gray-500">
                {isIOS ? 'Adicionar ao ecrã principal — Safari'
                  : isSamsung ? 'Adicionar ao ecrã — Samsung Internet'
                  : 'Adicionar ao ecrã — Chrome'}
              </p>
            </div>
          </div>
          <button
            onClick={() => setShowIOSModal(false)}
            className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center"
            data-testid="ios-modal-close"
            aria-label="Fechar"
          >
            <X className="h-4 w-4 text-gray-500" />
          </button>
        </div>

        {/* Steps */}
        <div className="px-5 pb-3 space-y-4">
          {steps.map((step, i) => {
            const c = colorMap[step.color];
            return (
              <React.Fragment key={step.num}>
                {i > 0 && (
                  <div className="flex justify-center">
                    <div className="w-px h-4 bg-gray-200" />
                  </div>
                )}
                <div className={`flex items-start gap-4 ${c.bg} border ${c.border} rounded-2xl p-4`}>
                  <div className={`flex-shrink-0 w-8 h-8 rounded-full ${c.circle} text-white text-sm font-bold flex items-center justify-center`}>
                    {step.num}
                  </div>
                  <div className="flex-1">
                    <p className="font-semibold text-gray-900 text-sm">{step.title}</p>
                    <p className="text-xs text-gray-500 mt-0.5">{step.sub}</p>
                  </div>
                  <div
                    className="flex-shrink-0 flex flex-col items-center gap-1"
                    style={{ animation: step.animation }}
                  >
                    <div className={`w-10 h-10 rounded-xl border-2 ${c.iconBorder} bg-white flex items-center justify-center shadow-sm`}>
                      {step.icon}
                    </div>
                    <p className={`text-[10px] font-bold ${step.color === 'blue' ? 'text-blue-600' : 'text-amber-600'}`}>
                      {step.iconLabel}
                    </p>
                  </div>
                </div>
              </React.Fragment>
            );
          })}
        </div>

        {/* CTA */}
        <div className="px-5 pt-2 pb-6 space-y-3">
          <div className="bg-[#0b1e44] rounded-2xl p-4 text-center">
            <p className="text-white text-sm font-semibold">{ctaText}</p>
            <p className="text-white/60 text-xs mt-1">
              Abre como uma app nativa · Sem espaço extra · Grátis
            </p>
          </div>
          <button
            onClick={confirmIOSInstall}
            className="w-full flex items-center justify-center gap-2 bg-emerald-50 border border-emerald-200 text-emerald-700 font-semibold text-sm rounded-2xl py-3.5 hover:bg-emerald-100 transition-colors"
            data-testid="ios-modal-confirm-install"
          >
            <CheckCircle className="h-4 w-4" />
            Já instalei a app
          </button>
        </div>
      </div>
    </>
  );
}
